import { isEqual } from 'lodash'
import { useCallback, useMemo, useRef, useState } from 'react'
import { type NavigateOptions, type Path, useLocation, useNavigate } from 'react-router'
import { type allRoutes, appRoutes } from './routes/routes'

export type AppRoute = (typeof allRoutes)[number]
export type AppView = AppRoute['id']
export type RenderedRoutes = ReturnType<AppRoute['make']>

export const useInternalAppNavigation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [history, setHistory] = useState<RenderedRoutes[]>([])
  const lastLocation = useRef<{
    to: RenderedRoutes
    options?: NavigateOptions
  }>(undefined)

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const navigateCb = useCallback((to?: RenderedRoutes, options?: NavigateOptions & { path?: Partial<Path> }): void | Promise<void> => {
    if (!to) {
      return
    }

    // reject multiple navigation requests to the same location
    if (lastLocation.current?.to.result === to.result && isEqual(lastLocation.current?.options, options)) {
      return
    }
    lastLocation.current = { to, options }
    setHistory((history) => [...history, to])
    if (options?.path) {
      return navigateRef.current({ ...options.path, pathname: to.result }, options)
    }
    return navigateRef.current(to.result, options)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: this function is implicitly impacted by location changes somehow
  const inView = useCallback(
    (...args: AppView[]) => {
      const location = lastLocation.current?.to
      if (!location) {
        return false
      }
      for (const arg of args) {
        if (location.id === arg) {
          return true
        }
      }
      return false
    },
    [location],
  )

  // hint: restoring the context could be done by going through all known routes and match them against the location

  return {
    location,
    appLocation: useMemo(() => history?.at(-1), [history]),
    history,
    routes: appRoutes,
    // forward navigation is just the result of all functions
    navigate: navigateCb,
    // impl notes: compare what is currently on top of the stack -> if it's the same, then go to a location until it's not the same view anymore
    // I don't like back. It's not good.
    // Back means, go one step back, but backing out of a view is so complicated with multiple parallel views due to the tabbing nature
    // if you want to go back, then just check yourself in you're in a view that you don't like and go back to your screens default view.
    // the screens view then itself is responsible for scrolling and whatever.
    // back: (from: AppView) => {},

    inView,
  }
}
